import { Checkbox, Grid, Typography } from "@mui/material";
import React, { useState } from "react";
import { orderSchema } from "./OrderSchema";
import { useForm, SubmitHandler, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ShowService from "@/app/_services/ShowService";

import {
  FirstNameInput,
  LastNameInput,
  NotesInput,
  PriceInput,
  QuantityInput,
} from "./OrderInputs";
import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import OrderService from "@/app/_services/OrderService";
import { Info as InfoIcon, TriangleAlert as WarningAmberIcon } from "lucide-react";
import { toast } from "@/app/_utils/toast";

interface OrderProps {
  disableOnSubmit: any;
  setDisableOnSubmit: any;
  show: any;
  order?: any;
  closeForm: any;
}
export default function OrderForm(props: OrderProps) {
  const [openWarning, setOpenWarning] = useState(false);
  const [adjustAvailable, setAdjustAvailable] = useState(false);

  const [makeAvailableWarning, setMakeAvailableWarning] = useState(false);

  const { disableOnSubmit, setDisableOnSubmit, show, order, closeForm } = props;

  type FormData = yup.InferType<typeof orderSchema>;
  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(orderSchema),
    defaultValues: {
      ...(order && {
        first_name: order.guest_data.first_name,
        last_name: order.guest_data.last_name,
        quantity: order.quantity,
        price: order.price,
        notes: order.notes,
      }),
    },
  });

  async function saveOrder(data: any) {
    setDisableOnSubmit(true);

    try {
      order
        ? await OrderService.updateOrderAsync(order.id, {
            ...data,
            adjustAvailable,
          })
        : await ShowService.addGuest({
            show_id: show.id,
            ...data,
          });

      toast.success("Incentive sucessfully created!");

      closeForm();
      window.location.reload();
    } catch (e) {
      toast.error("An error has occured");
    }

    setDisableOnSubmit(false);
  }

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    try {
      const { attending, capacity } = show;

      const passedCapacity = order
        ? data.quantity + attending - order.quantity > capacity
        : data.quantity + attending > capacity;

      if (passedCapacity) {
        setOpenWarning(true);
        return;
      }

      if (order && data.quantity != order.quantity) {
        setMakeAvailableWarning(true);
        return;
      }

      saveOrder(data);
    } catch (err) {}
  };

  const newCapacity =
    (order
      ? Math.abs(Number(getValues("quantity")) - order.quantity)
      : Number(getValues("quantity"))) + show.attending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="new-guest-form">
      <Grid container direction="column" p={1} rowGap={1.5}>
        <FirstNameInput
          control={control}
          errors={errors}
          disabled={disableOnSubmit || order}
        />

        <LastNameInput
          control={control}
          errors={errors}
          disabled={disableOnSubmit || order}
        />

        <QuantityInput
          control={control}
          errors={errors}
          disabled={disableOnSubmit || (order && !order.comped)}
        />

        <PriceInput
          control={control}
          errors={errors}
          disabled={disableOnSubmit || (order && !order.comped)}
        />

        <NotesInput
          control={control}
          errors={errors}
          disabled={disableOnSubmit}
        />

        <Grid
          container
          alignItems={"center"}
          columnGap={1}
          padding={1}
          mt={1}
          sx={{
            borderRadius: "5px",
            background: "var(--cuo-bg-hover)",
          }}
        >
          <InfoIcon />
          <Typography
            fontStyle={"italic"}
            fontSize={12}
            flex={1}
            sx={{ color: "var(--cuo-text-muted)" }}
          >
            Comping a guest will reduce the amount of available tickets for
            purchase by specified quantity
          </Typography>
        </Grid>
      </Grid>
      <CuoConfirmDialog
        open={openWarning}
        onClose={(e: any) => {
          setOpenWarning(false);
        }}
        icon={<WarningAmberIcon color="warning" />}
        title={"Capacity Warning"}
        message={
          <Typography>
            Capacity <strong>{show.capacity}</strong> cannot be less than the
            sum of sold, comped and available tickets{" "}
            <strong>{` ${newCapacity}`}</strong>. Do you want to increase
            capacity to <strong>{` ${newCapacity}`}</strong>?
          </Typography>
        }
        onConfirm={() => {
          const capacity =
            (order
              ? Math.abs(Number(getValues("quantity")) - order.quantity)
              : Number(getValues("quantity"))) + show.attending;
          saveOrder({ ...getValues(), capacity });
        }}
      />
      <CuoConfirmDialog
        open={makeAvailableWarning}
        onClose={(e: any) => {
          setMakeAvailableWarning(false);
          setAdjustAvailable(false);
        }}
        icon={<WarningAmberIcon color="warning" />}
        title={"Capacity Warning"}
        message={
          <Typography>
            You are changing the comped quantity from{" "}
            <strong>{order?.quantity}</strong> →{" "}
            <strong>{Number(getValues("quantity"))}</strong>. Do you want to
            adjust the available tickets?
            <Checkbox
              checked={adjustAvailable}
              onChange={(e: any) => setAdjustAvailable(e.target.checked)}
            />
          </Typography>
        }
        onConfirm={() => {
          saveOrder({ ...getValues() });
          setAdjustAvailable(false);
        }}
      />
    </form>
  );
}
